import { type FC, type ReactNode, useCallback, useState, type ChangeEvent } from 'react';

import { type UrlObject } from 'url';

import { type CheckboxProps } from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import { Link } from '@vakantiesnl/components/src/__LEGACY__/atoms/Link';
import { CheckboxFormField } from '@vakantiesnl/components/src/molecules/CheckboxFormField';
import { Toggle } from '@vakantiesnl/components/src/molecules/Toggle';
import { type FilterState } from '@vakantiesnl/services/src/stores/filtersStore';
import { type MicroCopy } from '@vakantiesnl/types';

import useStyles from './CheckboxGroup.style';

export type CheckBox = {
	name: keyof FilterState | 'durations';
	value: string;
	label: ReactNode;
	checked: boolean;
	counter?: number;
	hiddenLink?: UrlObject;
};

interface Props extends CheckboxProps {
	/** Aria label microcopy key */
	ariaLabel: string;
	/** Checkboxes to display */
	checkboxes: CheckBox[];
	/** Custom checkbox class for styling */
	checkboxClass?: string;
	/** Order by checked checkboxes */
	orderByChecked?: boolean;
	/** Microcopies */
	microCopies: MicroCopy;
	/** Amount of checkboxes shown in toggle */
	amountShown?: number;
	/** Filters to build hidden SEO link */
	disabled?: boolean;
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const CheckboxGroup: FC<Props> = ({
	ariaLabel,
	checkboxes,
	checkboxClass,
	orderByChecked = false,
	microCopies,
	amountShown,
	disabled,
	onChange,
}) => {
	const { classes, cx } = useStyles();
	const [showMore, setShowMore] = useState(false);
	const checkboxOptions = orderByChecked
		? checkboxes.sort((option, secondOption) => +secondOption.checked - +option.checked)
		: checkboxes;

	const handleToggle = useCallback(() => {
		setShowMore(!showMore);
	}, [showMore]);

	return (
		<FormGroup aria-label={microCopies[ariaLabel]} className={classes.checkboxGroup}>
			{checkboxOptions
				.slice(0, showMore ? checkboxOptions.length : amountShown)
				.filter(({ label }) => label)
				.map((checkbox, index) => {
					const { name, value, label, counter, checked, hiddenLink } = checkbox;

					const showCounter = !!(counter !== undefined && !isNaN(counter));

					return (
						<label key={value + index} className={cx(classes.checkboxItem, checkboxClass)}>
							<CheckboxFormField
								onChange={onChange}
								name={`${name}-${label}`}
								key={value}
								label={label as string}
								value={value}
								checked={checked}
								disabled={disabled}
								data-cy={`${name}-${label}`}
							/>
							{hiddenLink && (
								<Link href={hiddenLink} className={classes.hidden}>
									{label}
								</Link>
							)}
							{showCounter && <span className={classes.resultCount}>{counter}</span>}
						</label>
					);
				})}
			{amountShown && amountShown < checkboxOptions.length && (
				<Toggle
					isOpen={showMore}
					onClick={handleToggle}
					className={classes.toggleStyles}
					showMoreText={microCopies['filters.showMore']}
					showLessText={microCopies['filters.showLess']}
				/>
			)}
		</FormGroup>
	);
};
